import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Briefcase,
  Calendar,
  Building,
  MapPin,
  Hash,
  Clock,
  Banknote,
  TrendingUp,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtFt = (v) => (v == null ? "—" : `${Math.round(v).toLocaleString("hu-HU")} Ft`);

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.`;
  } catch {
    return iso;
  }
}

function employmentTypeLabel(key, t) {
  switch (key) {
    case "efo": return t("employment.type_efo");
    case "student_coop": return t("employment.type_student");
    case "full_time": return t("employment.type_full_time");
    case "part_time": return t("employment.type_part_time");
    default: return null;
  }
}

function employmentModeLabel(key, t) {
  switch (key) {
    case "partnership": return t("employment.type_company");
    case "mandate": return t("employment.type_assignment");
    case "entrepreneur": return t("employment.type_sole_trader");
    case "efo": return "EFO";
    case "student_coop": return t("employment.type_student");
    default: return null;
  }
}

function scheduleTypeLabel(key, t) {
  switch (key) {
    case "five_day": return t("employment.schedule_five_day");
    case "variable": return t("employment.schedule_variable");
    default: return null;
  }
}

function statusMeta(key, t) {
  switch (key) {
    case "active":
      return { label: t("employment.active"), color: "emerald" };
    case "probation":
      return { label: t("employment.probation"), color: "amber" };
    case "notice":
      return { label: t("employment.notice_period"), color: "amber" };
    case "inactive":
      return { label: t("employment.inactive"), color: "zinc" };
    default:
      return { label: t("employment.active"), color: "emerald" };
  }
}

export default function PortalEmployment() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [disc, setDisc] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [e, d] = await Promise.all([
          api.get("/portal/employment"),
          api.get("/portal/disciplinary").catch(() => ({ data: { items: [] } })),
        ]);
        setData(e.data);
        setDisc(d.data?.items || []);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !data) {
    return (
      <div className="space-y-3 px-4 pb-8 pt-6 md:px-6 md:pt-8">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />
        ))}
      </div>
    );
  }

  const sm = statusMeta(data.status, t);
  const empTypeLbl = employmentTypeLabel(data.employment_type, t);

  return (
    <div className="space-y-6 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      <header>
        <p className="text-[14px] text-zinc-500">{t("nav.employment")}</p>
        <h1 className="mt-1 text-[28px] font-semibold tracking-tight md:text-[32px]">
          {data.position || "—"}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusPill meta={sm} />
          {empTypeLbl && (
            <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-1 text-[11.5px] font-medium text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              {empTypeLbl}
            </span>
          )}
        </div>
      </header>

      {/* Hire date hero */}
      <section className="rounded-3xl bg-white p-6 shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_2px_6px_-2px_rgba(0,0,0,0.04)] dark:bg-zinc-900 md:p-7">
        <p className="text-[13px] text-zinc-500">{t("employment.start_date")}</p>
        <p className="mt-1.5 text-[28px] font-semibold tracking-tight md:text-[32px]">
          {fmtDate(data.employment_start_date) || "—"}
        </p>
        {data.probation_end_date && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            <Clock className="h-3 w-3" strokeWidth={2.2} />
            {t("employment.probation_end")}: {fmtDate(data.probation_end_date)}
          </p>
        )}
      </section>

      {/* Cég / telephely */}
      {(data.company_name || data.site_name) && (
        <Section title={`${t("profile.company")} & ${t("profile.site")}`}>
          {data.company_name && <Row icon={Building} label={t("profile.company")} value={data.company_name} />}
          {data.site_name && <Row icon={MapPin} label={t("profile.site")} value={data.site_name} />}
          {data.site_address && <Row icon={MapPin} label={t("profile.address")} value={data.site_address} />}
        </Section>
      )}

      {/* Foglalkoztatás */}
      <Section title={t("employment.details")}>
        <Row icon={Briefcase} label={t("employment.position")} value={data.position} />
        {data.employment_mode && (
          <Row
            icon={Briefcase}
            label={t("employment.legal_status")}
            value={employmentModeLabel(data.employment_mode, t) || data.employment_mode}
          />
        )}
        {data.schedule_type && (
          <Row
            icon={Calendar}
            label={t("employment.schedule_type")}
            value={scheduleTypeLabel(data.schedule_type, t) || data.schedule_type}
          />
        )}
        {data.weekly_hours != null && (
          <Row icon={Clock} label={t("employment.weekly_hours")} value={t("employment.hours_value", { count: data.weekly_hours })} />
        )}
        {data.feor_number && (
          <Row icon={Hash} label={t("employment.feor")} value={data.feor_number} mono />
        )}
      </Section>

      {/* Bérezés */}
      {(data.gross_hourly_rate != null || data.gross_monthly_salary != null || data.default_gross_commission != null) && (
        <Section title={t("employment.pay")}>
          {data.gross_monthly_salary != null && (
            <Row
              icon={Banknote}
              label={t("employment.gross_base_salary")}
              value={fmtFt(data.gross_monthly_salary)}
              mono
            />
          )}
          {data.gross_hourly_rate != null && (
            <Row
              icon={Banknote}
              label={t("employment.gross_hourly")}
              value={fmtFt(data.gross_hourly_rate)}
              mono
            />
          )}
          {data.default_gross_commission != null && (
            <Row
              icon={TrendingUp}
              label={t("employment.commission_base")}
              value={fmtFt(data.default_gross_commission)}
              mono
            />
          )}
          {data.payment_method && (
            <Row
              icon={Banknote}
              label={t("employment.payment_method")}
              value={
                data.payment_method === "cash"
                  ? t("employment.payment_cash")
                  : data.payment_method === "transfer"
                  ? t("employment.payment_transfer")
                  : data.payment_method
              }
            />
          )}
        </Section>
      )}

      {/* Fegyelmik */}
      <section>
        <div className="mb-3 flex items-center justify-between px-2">
          <h2 className="flex items-center gap-1.5 text-[15px] font-semibold tracking-tight">
            <ShieldAlert
              className={
                "h-4 w-4 " +
                (disc.length > 0
                  ? "text-red-500 dark:text-red-400"
                  : "text-zinc-400")
              }
              strokeWidth={2}
            />
            {t("employment.disciplinary_entries")}
          </h2>
          <span
            className={
              "rounded-full px-2 py-0.5 text-[11.5px] font-medium " +
              (disc.length > 0
                ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400")
            }
          >
            {t("drafts.items_count", { count: disc.length })}
          </span>
        </div>
        {disc.length === 0 ? (
          <div className="rounded-2xl bg-white p-8 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <div className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
              <ShieldAlert className="h-4 w-4" strokeWidth={2} />
            </div>
            <p className="mt-3 text-[13.5px] text-zinc-500">
              {t("employment.no_disciplinary")}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
            {disc.map((d, i) => (
              <DiscRow key={d.id} record={d} last={i === disc.length - 1} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusPill({ meta }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    zinc: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
  };
  return (
    <span className={"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium " + (map[meta.color] || map.zinc)}>
      <span
        className={
          "h-1.5 w-1.5 rounded-full " +
          (meta.color === "emerald"
            ? "bg-emerald-500"
            : meta.color === "amber"
            ? "bg-amber-500"
            : "bg-zinc-400")
        }
      />
      {meta.label}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="mb-2.5 px-2">
        <h2 className="text-[13px] font-medium text-zinc-500">{title}</h2>
      </div>
      <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
        {children}
      </div>
    </section>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 first:pt-4 last:pb-4 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-zinc-100 dark:[&:not(:first-child)]:border-zinc-800/60">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-zinc-100 text-zinc-500 dark:bg-zinc-800/60 dark:text-zinc-400">
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] text-zinc-500">{label}</p>
        <p className={"mt-0.5 truncate text-[14px] tracking-tight " + (mono ? "font-mono tabular-nums" : "")}>
          {value}
        </p>
      </div>
    </div>
  );
}

function DiscRow({ record, last }) {
  const { t } = useTranslation();
  return (
    <div className={"px-5 py-4 " + (last ? "" : "border-b border-zinc-100 dark:border-zinc-800/60")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold tracking-tight">
            {record.reason || record.category || t("employment.entry")}
          </p>
          {record.notes && (
            <p className="mt-1 text-[12.5px] text-zinc-500">{record.notes}</p>
          )}
          {record.issuer && (
            <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
              {record.issuer}
            </p>
          )}
        </div>
        <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-zinc-400">
          {record.issued_at || "—"}
        </span>
      </div>
    </div>
  );
}
